import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-600 transition-colors duration-200"
          >
            <span className="font-heading text-xl md:text-2xl font-bold whitespace-nowrap">
              {t("nav.brand")}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-menu-line w-4 h-4 flex items-center justify-center"></i>
                <span>{t("nav.categories")}</span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden shadow-lg animate-[fadeIn_0.2s_ease-out] z-50">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                      >
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t("hero.cta.pandabuy")}
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-[fadeIn_0.2s_ease-out] max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2 mb-2">
              {t("nav.allCategories")}
            </p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150"
              >
                <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                {cat.name}
              </a>
            ))}
            <div className="pt-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200"
              >
                {t("hero.cta.pandabuy")}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}